if (!customElements.get('bulk-tier-selector')) {
  class BulkTierSelector extends HTMLElement {
    constructor() {
      super();
      this.qty = 1;
      this.basePriceCents = 0;
      this.variantSku = '';
      this.tiers = [];
      this.config = [];
      this.skuLookup = new Map();
      this.i18n = {};
      this.isActive = false;
      this.originalBtnText = '';
      this.selectedTierQty = null;
      this.variantChangeUnsub = undefined;
      this.quantityUpdateUnsub = undefined;
    }

    connectedCallback() {
      const configNode = this.querySelector('.bulk-tier-selector__config');
      const i18nNode = this.querySelector('.bulk-tier-selector__i18n');

      try {
        this.config = JSON.parse(configNode?.textContent || '[]');
      } catch {
        this.config = [];
      }

      this.buildSkuLookup();

      try {
        this.i18n = JSON.parse(i18nNode?.textContent || '{}');
      } catch {
        this.i18n = {};
      }

      this.sectionId = this.dataset.sectionId;
      this.form = document.getElementById(this.dataset.formId);
      this.tiersGrid = this.querySelector('.bulk-tier-selector__tiers');
      this.savingsBanner = this.querySelector('.bulk-tier-selector__savings');
      this.savingsText = this.querySelector('.bulk-tier-selector__savings-text');
      this.qtyDisplay = this.querySelector('.bulk-tier-selector__qty-value');
      this.qtyRow = this.querySelector('.bulk-tier-selector__qty-row');

      this.variantSku = this.dataset.variantSku || '';
      this.basePriceCents = parseInt(this.dataset.variantPrice, 10) || 0;
      this.compareAtPriceCents = parseInt(this.dataset.variantCompareAtPrice, 10) || 0;

      this.setupQtyButtons();
      this.saveOriginalBtnText();

      this.variantChangeUnsub = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
        this.onVariantChange.bind(this)
      );

      this.quantityUpdateUnsub = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.quantityUpdate,
        this.onQuantityUpdate.bind(this)
      );

      this.apply();
    }

    disconnectedCallback() {
      if (this.variantChangeUnsub) this.variantChangeUnsub();
      if (this.quantityUpdateUnsub) this.quantityUpdateUnsub();
    }

    onVariantChange({ data }) {
      if (!data || data.sectionId !== this.sectionId) return;
      const variant = data.variant;
      this.form = document.getElementById(this.dataset.formId);
      if (!variant) {
        this.deactivate();
        return;
      }
      this.variantSku = variant.sku || '';
      this.basePriceCents = variant.price || 0;
      this.compareAtPriceCents = variant.compare_at_price || 0;
      this.apply();
    }

    onQuantityUpdate() {
      if (!this.isActive) return;
      const input = this.getQuantityInput();
      if (!input) return;
      const parsed = parseInt(input.value, 10);
      if (!parsed || parsed === this.qty) return;
      this.qty = parsed;
      this.syncTierSelectionWithQty();
      this.updateUI();
    }

    setupQtyButtons() {
      this.querySelectorAll('.bulk-tier-selector__qty-btn').forEach((button) => {
        button.addEventListener('click', () => {
          const delta = button.dataset.action === 'plus' ? 1 : -1;
          this.changeQty(delta);
        });
      });
    }

    saveOriginalBtnText() {
      const btn = this.getSubmitButton();
      if (btn) {
        const textEl = btn.querySelector('.btn__text');
        this.originalBtnText = textEl?.innerHTML?.trim() || textEl?.textContent?.trim() || '';
      }
    }

    getSubmitButton() {
      return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    }

    getQuantityInput() {
      return this.form?.querySelector('input[name="quantity"]');
    }

    getButtonsRow() {
      return this.form?.querySelector('.product-form__buttons');
    }

    buildSkuLookup() {
      this.skuLookup = new Map();

      for (const entry of this.config) {
        const skus = Array.isArray(entry.skus) ? entry.skus : entry.sku ? [entry.sku] : [];
        for (const sku of skus) {
          const normalized = (sku || '').trim().toLowerCase();
          if (normalized) this.skuLookup.set(normalized, entry);
        }
      }
    }

    findConfigEntry() {
      const sku = (this.variantSku || '').trim().toLowerCase();
      return sku ? this.skuLookup.get(sku) || null : null;
    }

    hasCompareAtPrice() {
      return this.compareAtPriceCents > 0 && this.compareAtPriceCents > this.basePriceCents;
    }

    buildTiers(entry) {
      const tiers = (entry.tiers || [])
        .map((tier) => ({
          quantity: parseInt(tier.quantity, 10),
          discount: parseFloat(tier.discount) || 0,
          badge: tier.badge || null,
        }))
        .filter((tier) => tier.quantity > 0)
        .sort((a, b) => a.quantity - b.quantity);

      const hasBase = tiers.some((tier) => tier.quantity === 1);
      if (!hasBase) {
        tiers.unshift({ quantity: 1, discount: 0, badge: null });
      }

      return tiers;
    }

    clearTierSelection() {
      this.selectedTierQty = null;
    }

    apply() {
      if (this.hasCompareAtPrice()) {
        this.deactivate();
        return;
      }

      const entry = this.findConfigEntry();
      if (!entry) {
        this.deactivate();
        return;
      }

      this.tiers = this.buildTiers(entry);
      if (!this.tiers.length) {
        this.deactivate();
        return;
      }

      this.activate();
      this.clearTierSelection();
      this.qty = 1;
      this.renderTiers();
      this.setQuantityInput(this.qty);
      this.updateUI();
    }

    activate() {
      this.isActive = true;
      this.classList.remove('hidden');
      this.classList.add('is-active');

      this.notifyStickyBarUpdate();
      this.updatePageLayout(true);
    }

    notifyStickyBarUpdate() {
      window.dispatchEvent(new CustomEvent('bulk-tier:observer-update'));
    }

    getProductInfoEl() {
      return document.getElementById(`MainProduct-${this.sectionId}`);
    }

    updatePageLayout(active) {
      const productInfo = this.getProductInfoEl();
      if (!productInfo) return;
      productInfo.classList.toggle('bulk-tier-page-active', active);
    }

    deactivate() {
      this.isActive = false;
      this.classList.add('hidden');
      this.classList.remove('is-active');
      this.tiers = [];

      const btn = this.getSubmitButton();
      if (btn && this.originalBtnText) {
        const textEl = btn.querySelector('.btn__text');
        if (textEl) textEl.innerHTML = this.getDefaultButtonText();
      }

      this.savingsBanner?.classList.add('hidden');

      this.notifyStickyBarUpdate();
      this.updatePageLayout(false);
    }

    renderTiers() {
      if (!this.tiersGrid) return;
      this.tiersGrid.innerHTML = '';
      this.tiersGrid.dataset.tierCount = String(this.tiers.length);
      this.tiersGrid.style.setProperty('--bulk-tier-count', String(this.tiers.length));

      this.tiers.forEach((tier) => {
        const card = document.createElement('div');
        card.className = 'bulk-tier-selector__tier';
        card.dataset.quantity = String(tier.quantity);
        card.setAttribute('role', 'button');
        card.tabIndex = 0;

        card.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            card.click();
          }
        });

        if (tier.badge === 'most_popular') {
          card.classList.add('has-badge-most_popular');
        }
        if (tier.badge === 'best_value') {
          card.classList.add('has-badge-best_value');
        }

        if (tier.badge === 'most_popular') {
          const badge = document.createElement('div');
          badge.className = 'bulk-tier-selector__tier-badge bulk-tier-selector__tier-badge--popular';
          badge.textContent = this.i18n.badge_most_popular || 'MOST POPULAR';
          card.appendChild(badge);
        } else if (tier.badge === 'best_value') {
          const badge = document.createElement('div');
          badge.className = 'bulk-tier-selector__tier-badge bulk-tier-selector__tier-badge--value';
          badge.textContent = this.i18n.badge_best_value || 'BEST VALUE';
          card.appendChild(badge);
        }

        const label = document.createElement('p');
        label.className = 'bulk-tier-selector__tier-label';
        label.textContent = this.packLabel(tier.quantity).toUpperCase();
        card.appendChild(label);

        if (tier.discount > 0) {
          const discount = document.createElement('span');
          discount.className = 'bulk-tier-selector__tier-discount';
          if (tier.badge === 'best_value') {
            discount.classList.add('bulk-tier-selector__tier-discount--value');
          } else {
            discount.classList.add('bulk-tier-selector__tier-discount--popular');
          }
          discount.textContent = this.formatDiscount(tier.discount);
          card.appendChild(discount);
        }

        const price = document.createElement('p');
        price.className = 'bulk-tier-selector__tier-price';
        card.appendChild(price);

        const perPack = document.createElement('p');
        perPack.className = 'bulk-tier-selector__tier-per-pack';
        card.appendChild(perPack);

        const divider = document.createElement('div');
        divider.className = 'bulk-tier-selector__tier-divider';
        card.appendChild(divider);

        const total = document.createElement('p');
        total.className = 'bulk-tier-selector__tier-total';
        card.appendChild(total);

        card.addEventListener('click', () => {
          this.selectedTierQty = tier.quantity;
          this.qty = tier.quantity;
          this.setQuantityInput(this.qty);
          this.updateUI();
        });

        this.tiersGrid.appendChild(card);
      });
    }

    getApplicableTier(qty) {
      let applicable = null;

      for (const tier of this.tiers) {
        if (qty >= tier.quantity && tier.discount > 0) {
          if (!applicable || tier.quantity > applicable.quantity) {
            applicable = tier;
          }
        }
      }

      return applicable;
    }

    isDefaultSelection() {
      return !this.getApplicableTier(this.qty);
    }

    syncTierSelectionWithQty() {
      if (this.qty === 1) {
        this.selectedTierQty = null;
      } else if (this.selectedTierQty !== null && this.qty !== this.selectedTierQty) {
        this.selectedTierQty = null;
      }
    }

    getDefaultButtonText() {
      return this.originalBtnText || FoxTheme?.variantStrings?.addToCart || 'Add to cart';
    }

    restoreDefaultButton() {
      const btn = this.getSubmitButton();
      const textEl = btn?.querySelector('.btn__text');
      if (textEl) textEl.innerHTML = this.getDefaultButtonText();
    }

    formatMoney(cents) {
      return FoxTheme.Currency.formatMoney(cents, FoxTheme.settings.moneyFormat);
    }

    formatMoneyPlain(cents) {
      const formatted = this.formatMoney(cents);
      const temp = document.createElement('span');
      temp.innerHTML = formatted;
      return temp.textContent || formatted;
    }

    formatDiscount(percent) {
      const template = this.i18n.discount || '−{{ percent }}%';
      return template.replace('{{ percent }}', String(Math.round(percent)));
    }

    getDiscountedPriceCents(discountPercent) {
      if (!discountPercent) return this.basePriceCents;
      const discountAmount = Math.floor((this.basePriceCents * discountPercent) / 100);
      return this.basePriceCents - discountAmount;
    }

    getSavedCents(discountPercent, qty) {
      if (!discountPercent) return 0;
      return Math.floor((this.basePriceCents * discountPercent) / 100) * qty;
    }

    packLabel(count) {
      const template = count === 1 ? this.i18n.pack_one : this.i18n.pack_other;
      return (template || '{{ count }} pack').replace('{{ count }}', String(count));
    }

    formatTotal(amountCents) {
      const template = this.i18n.total || '{{ amount }} total';
      return template.replace('{{ amount }}', this.formatMoneyPlain(amountCents));
    }

    setQuantityInput(value) {
      const input = this.getQuantityInput();
      if (!input) return;
      input.value = value;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.quantityUpdate, undefined);
    }

    changeQty(delta) {
      const input = this.getQuantityInput();
      const min = input ? parseInt(input.getAttribute('min') || '1', 10) : 1;
      const maxAttr = input?.getAttribute('max');
      const max = maxAttr ? parseInt(maxAttr, 10) : null;
      let next = Math.max(min, this.qty + delta);
      if (max) next = Math.min(max, next);
      this.qty = next;
      this.syncTierSelectionWithQty();
      this.setQuantityInput(this.qty);
      this.updateUI();
    }

    updateUI() {
      if (!this.isActive) return;

      const applicableTier = this.getApplicableTier(this.qty);
      const discountPercent = applicableTier?.discount || 0;
      const pricePerPackCents = this.getDiscountedPriceCents(discountPercent);
      const totalCents = pricePerPackCents * this.qty;
      const savedCents = this.getSavedCents(discountPercent, this.qty);
      const highlightTierQty =
        this.selectedTierQty ?? (applicableTier ? applicableTier.quantity : null);

      if (this.qtyDisplay) this.qtyDisplay.textContent = String(this.qty);

      this.tiersGrid?.querySelectorAll('.bulk-tier-selector__tier').forEach((card) => {
        const cardQty = parseInt(card.dataset.quantity, 10);
        const cardTier = this.tiers.find((t) => t.quantity === cardQty);
        if (!cardTier) return;

        const cardDiscountPercent = cardTier.discount || 0;
        const cardPriceCents = this.getDiscountedPriceCents(cardDiscountPercent);
        const cardTotalCents = cardPriceCents * cardQty;

        card.classList.remove(
          'is-selected',
          'is-selected--default',
          'is-selected--popular',
          'is-selected--value'
        );

        const isSelected = highlightTierQty !== null && cardQty === highlightTierQty;
        if (isSelected) {
          const activeTier = this.tiers.find((tier) => tier.quantity === highlightTierQty) || cardTier;
          card.classList.add('is-selected');
          if (activeTier.badge === 'best_value') {
            card.classList.add('is-selected--value');
          } else if (activeTier.badge === 'most_popular') {
            card.classList.add('is-selected--popular');
          } else {
            card.classList.add('is-selected--default');
          }
        }

        const priceEl = card.querySelector('.bulk-tier-selector__tier-price');
        const perPackEl = card.querySelector('.bulk-tier-selector__tier-per-pack');
        const totalEl = card.querySelector('.bulk-tier-selector__tier-total');

        if (priceEl) priceEl.innerHTML = this.formatMoney(cardPriceCents);
        if (perPackEl) {
          perPackEl.innerHTML =
            cardDiscountPercent > 0
              ? `${this.formatMoney(this.basePriceCents)}${this.i18n.per_pack || '/pack'}`
              : this.i18n.per_pack || '/pack';
          perPackEl.classList.toggle('is-struck', cardDiscountPercent > 0);
        }
        if (totalEl) totalEl.textContent = this.formatTotal(cardTotalCents);
      });

      if (this.savingsBanner) {
        if (applicableTier && discountPercent > 0 && savedCents > 0) {
          this.savingsBanner.classList.remove('hidden');
          this.savingsBanner.classList.toggle('is-value', applicableTier.badge === 'best_value');
          this.savingsBanner.classList.toggle('is-popular', applicableTier.badge !== 'best_value');

          const saved = this.formatMoneyPlain(savedCents);
          if (this.savingsText) {
            this.savingsText.innerHTML = (this.i18n.you_save || 'You save {{ amount }}').replace(
              '{{ amount }}',
              `<strong>${saved}</strong>`
            );
          }
        } else {
          this.savingsBanner.classList.add('hidden');
        }
      }

      if (this.isDefaultSelection()) {
        this.restoreDefaultButton();
      } else {
        const btn = this.getSubmitButton();
        const textEl = btn?.querySelector('.btn__text');
        if (textEl) {
          const packs = this.packLabel(this.qty);
          const cta = (this.i18n.add_to_cart || 'Add {{ packs }} to cart — {{ amount }}')
            .replace('{{ packs }}', packs)
            .replace('{{ amount }}', this.formatMoneyPlain(totalCents));
          textEl.textContent = cta;
        }
      }
    }
  }

  customElements.define('bulk-tier-selector', BulkTierSelector);
}
